import { Form, Formik } from 'formik';
import * as yup from 'yup';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FileUpload } from '@/components/molecules/fileUpload';
import { ButtonLoading } from '@/components/ui/button';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import type { AvailableCity } from '@/graphql/types/chatKnowledge';

interface UploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: UploadFormValues) => Promise<void>;
  loading: boolean;
  availableCities: AvailableCity[];
}

export interface UploadFormValues {
  file: File | null;
  category: string;
  municipality_code: string;
}

const validationSchema = yup.object().shape({
  file: yup
    .mixed<File>()
    .required('Arquivo é obrigatório')
    .test('fileSize', 'Arquivo muito grande (máximo 10MB)', (value) => {
      if (!value) return false;
      return value.size <= 10 * 1024 * 1024;
    }),
  category: yup.string().required('Categoria é obrigatória'),
  municipality_code: yup.string().optional(),
});

const initialValues: UploadFormValues = {
  file: null,
  category: 'document',
  municipality_code: '',
};

export const UploadDialog = ({
  open,
  onOpenChange,
  onSubmit,
  loading,
  availableCities,
}: UploadDialogProps) => {
  const handleSubmit = async (values: UploadFormValues) => {
    if (!values.file) return;
    await onSubmit(values);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-6">
        <DialogHeader className="w-fit">
          <DialogTitle>Upload de Arquivo</DialogTitle>
        </DialogHeader>

        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ values, errors, setFieldValue, resetForm }) => (
            <Form className="flex flex-col gap-6 w-full">
              <FileUpload
                id="file"
                name="file"
                label="Arquivo"
                accept=".pdf,.doc,.docx,.txt,.md"
                maxSizeMB={10}
                value={values.file}
                onChange={(file) => setFieldValue('file', file)}
                error={Boolean(errors.file)}
                helperText={errors.file as string}
                className="w-full max-w-full"
              />

              <div className="flex flex-col gap-2 w-full max-w-full">
                <Label htmlFor="category">Categoria</Label>
                <Select
                  name="category"
                  value={values.category}
                  onValueChange={(value) => setFieldValue('category', value)}
                >
                  <SelectTrigger className="h-8 w-full">
                    <SelectValue placeholder="Selecione a categoria" />
                  </SelectTrigger>
                  <SelectContent side="bottom">
                    <SelectItem value="document">Documento</SelectItem>
                    <SelectItem value="report">Relatório</SelectItem>
                    <SelectItem value="guide">Guia</SelectItem>
                    <SelectItem value="other">Outro</SelectItem>
                  </SelectContent>
                </Select>
                {errors.category && (
                  <p className="text-red-500 text-xs">{errors.category}</p>
                )}
              </div>

              <div className="flex flex-col gap-2 w-full max-w-full">
                <Label htmlFor="municipality_code">Município (Opcional)</Label>
                <Select
                  name="municipality_code"
                  value={values.municipality_code || undefined}
                  onValueChange={(value) => setFieldValue('municipality_code', value || '')}
                >
                  <SelectTrigger className="h-8 w-full">
                    <SelectValue placeholder="Selecione o município (opcional)" />
                  </SelectTrigger>
                  <SelectContent side="bottom">
                    {availableCities.map((city) => (
                      <SelectItem key={city.municipality_code} value={city.municipality_code}>
                        {city.name_municipality} - {city.sigla_uf}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter className="flex-col">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    resetForm();
                    onOpenChange(false);
                  }}
                >
                  Cancelar
                </Button>

                <ButtonLoading
                  variant="default"
                  label="Upload"
                  loading={loading}
                  type="submit"
                  disabled={loading || !values.file || Boolean(errors.file)}
                />
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
